/**
 * API клієнт для doc.product_assemblies — Комплектація номенклатури.
 * Permission: production.assembly.manage (single на CRUD + post).
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface ProductAssemblyComponentIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  uom_id?: string | null;
}

export interface ProductAssemblyComponent extends ProductAssemblyComponentIn {
  id: string;
  cost_per_unit: string | null;
  total_cost: string | null;
}

export interface ProductAssemblyListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_id: string;
  assembled_nomenclature_id: string;
  assembled_quantity: string;
  total_cost: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  // Block 5.3c:
  state?: CancellableState;
}

export interface ProductAssembly extends ProductAssemblyListItem {
  posted_at: string | null;
  comment: string | null;
  project_id: string | null;
  components: ProductAssemblyComponent[];
  // Block 5.3c:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface ProductAssemblyCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  warehouse_id: string;
  assembled_nomenclature_id: string;
  assembled_quantity: string;
  comment?: string | null;
  project_id?: string | null;
  components?: ProductAssemblyComponentIn[];
}

export type ProductAssemblyUpdate = Partial<Omit<ProductAssemblyCreate, 'organization_id'>>;

export const productAssemblies = {
  list: (params?: { organization_id?: string; posted?: boolean; limit?: number; offset?: number }) =>
    api.get<Page<ProductAssemblyListItem>>('/product-assemblies', { params }).then((r) => r.data),
  get: (id: string) => api.get<ProductAssembly>(`/product-assemblies/${id}`).then((r) => r.data),
  create: (payload: ProductAssemblyCreate) =>
    api.post<ProductAssembly>('/product-assemblies', payload).then((r) => r.data),
  update: (id: string, payload: ProductAssemblyUpdate) =>
    api.patch<ProductAssembly>(`/product-assemblies/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/product-assemblies/${id}`).then(() => undefined),
  post: (id: string) => api.post<ProductAssembly>(`/product-assemblies/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<ProductAssembly>(`/product-assemblies/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/product-assemblies/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
